import type { ChangeEvent, ReactNode } from 'react';
import { forwardRef, useState, useEffect } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { validateData, type ErrorObject } from '../../providers/validateData';
import CustomTypography from '../Typography/CustomTypography';

interface ValidationError {
  onValidationError?: (error: ErrorObject | null) => void;
  validateRules?: 'required'[];
}

interface CustomCheckboxProps extends ValidationError {
  id: string;
  className?: string;
  label?: string;
  checked?: boolean;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  disabled?: boolean;
  icon?: ReactNode;
  checkedIcon?: ReactNode;
  size?: 'small' | 'medium';
  required?: boolean;
  clearFilter?: boolean;
}

const CustomCheckbox = forwardRef<HTMLInputElement, CustomCheckboxProps>(
  (
    {
      id,
      label,
      className,
      checked = false,
      labelPlacement = 'end',
      onChange,
      color = 'primary',
      disabled = false,
      icon,
      checkedIcon,
      size = 'small',
      required = false,
      validateRules,
      onValidationError,
      clearFilter,
    },
    ref
  ) => {
    const [isChecked, setIsChecked] = useState<boolean>(checked);
    const [inputError, setInputError] = useState<ErrorObject | null>(null);
    useEffect(() => {
      if (clearFilter) {
        setIsChecked(false);
      }
      setIsChecked(checked);
    }, [checked, clearFilter]);

    const handleChange = (
      event: ChangeEvent<HTMLInputElement>,
      isChecked: boolean
    ) => {
      setIsChecked(isChecked);
      if (onChange) {
        onChange(event, isChecked);
      }

      if (validateRules) {
        const errors = validateData(isChecked, validateRules);
        if (onValidationError) {
          onValidationError(errors);
        }
        setInputError(errors);
      }
    };

    return (
      <>
        <FormControlLabel
          labelPlacement={labelPlacement}
          control={
            <Checkbox
              id={id}
              checked={isChecked}
              className={className}
              onChange={handleChange}
              color={color}
              disabled={disabled}
              icon={icon}
              checkedIcon={checkedIcon}
              size={size}
              inputRef={ref}
              required={required}
            />
          }
          label={label || ''}
        />
        {inputError?.invalid && (
          <CustomTypography id={`${id}-error`} variant="h6" color="error">
            {inputError.message}
          </CustomTypography>
        )}
      </>
    );
  }
);

CustomCheckbox.displayName = 'CustomCheckbox';

export default CustomCheckbox;
